import {Injectable}                                                       from '@angular/core';
import {IPpPlanningBookingGridItem, ISummaryWithPlanBooks, PpNoDataValue} from '../types/pp-planning-booking.types';
import {FieldStore}                                                       from '../../stores/farm/field.store';
import {
  ApTooltipContentService
}                                                                         from '../../ap-utils/service/ap-tooltip-content.service';
import {combineLatest, Observable, Subscription}                          from 'rxjs';
import {filter, map}                                                      from 'rxjs/operators';
import {
  PlantProtectionPlanBookStore
}                                                                         from '../../stores/plant-protection/plant-protection-planbook.store';
import {
  CropTypeStore
}                                                                         from '../../stores/base-data/crop.types.store';
import {
  ApDynComponentComponent
}                                                                         from '../../ap-dyncomponent/ap-dyncomponent.component';
import {CampaignYearStore}                                                from '../../stores/login/campaignyear.store';
import {
  ApTranslationService
}                                                                         from '../../ap-utils/service/ap-translation.service';
import {
  ApNutrientService
}                                                                         from '../../ap-utils/service/ap-nutrient.service';
import {
  CropApplicationStore
}                                                                         from '../../stores/task-management/crop-application.store';
import {PpCommonService}                                                  from './pp-common.service';
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import {SensorStore}                                                      from '../../stores/sensor/sensor.store';
import {
  GetRoundNumericService
}                                                                         from '../../ap-utils/service/get-round-numeric.service';
import {SettingsStore}                                                    from '../../stores/base-data/settings.store';
import {MapStore}                                                         from '../../stores/map/map.store';
import {RouterStore}                                                      from '../../stores/router/router.store';
import {
  ApShowPointsOnMapService
}                                                                         from '../../ap-utils/service/ap-show-points-on-map.service';
import {PlanBooksService}                                                 from '../../services/data/plan-books.service';

/**
 * Service for plant protection planning and booking components.
 * Plant-Protection -> Plans
 * Plant-Protection -> Records
 */
@Injectable({
  providedIn: 'root'
})
export class PpPlanningBookingDataService {

  private _subscriptions: Subscription[] = [];
  private _isBooked = false;

  constructor(private fieldStore: FieldStore,
              private cropTypeStore: CropTypeStore,
              private sensorStore: SensorStore,
              private mapStore: MapStore,
              private routerStore: RouterStore,
              private cropApplicationStore: CropApplicationStore,
              private plantProtectionPlanBooksStore: PlantProtectionPlanBookStore,
              private campaignYearStore: CampaignYearStore,
              private translationService: ApTranslationService,
              private roundNumericService: GetRoundNumericService,
              private settingsStore: SettingsStore,
              private nutrientService: ApNutrientService,
              private ppCommonService: PpCommonService,
              private showPointsOnMap: ApShowPointsOnMapService,
              private planBooksService: PlanBooksService,
              private tooltipContentService: ApTooltipContentService) {
  }

  /**
   * Initializes the service to be used for plannings or bookings
   * @param isBooking indicates if it is used for bookings
   */
  public init(isBooking: boolean): void {
    this._isBooked = isBooking;
  }

  /**
   * Builds the dataItems for the main grid of a planning and booking component
   */
  public buildDataItems(): Observable<IPpPlanningBookingGridItem[]> {
    return combineLatest([
      this.plantProtectionPlanBooksStore.Listen(s => s.data),
      this.cropTypeStore.Listen(s => s.cropTypesNames),
      this.fieldStore.Listen(s => s.data),
      this.sensorStore.Listen(s => s.data),
      this.cropApplicationStore.Listen(s => s.data)
    ]).pipe(
      filter(([planBooks, cropTypesNames, fields, sensor, _]) =>
        !!planBooks && !!cropTypesNames && !!fields && !!sensor),
      map(([planBooks, cropTypesNames, _, _1]) => {
          const summaries = this._getSummaryWithPlanBooks(planBooks, this._isBooked);
          return summaries.filter(x => x.FilteredPlanBooks && x.FilteredPlanBooks.length > 0)
            .map<IPpPlanningBookingGridItem>(summaryWithPlanBooks => {
              const summary = summaryWithPlanBooks.Summary;
              const filteredPlanBooks = summaryWithPlanBooks.FilteredPlanBooks;
              const totalPlanBooks = summaryWithPlanBooks.TotalPlanBooks;
              const area = filteredPlanBooks.reduce((a, b) => a + (this.fieldStore.getFieldGeomById(b.FieldGeomId)?.AreaHa ?? 0), 0);
              const nUptakesByArea = filteredPlanBooks.map(pb =>
                (this.sensorStore.getSensorFieldData(pb.FieldGeomId)?.NUptakeAvg ?? 0) * (this.fieldStore.getFieldGeomById(pb.FieldGeomId)?.AreaHa ?? 0));
              const nUptakeAverage = this.roundNumericService.roundAsNumber((nUptakesByArea.reduce((a, b) => a + b, 0) / area), this.settingsStore.FirstSetting.DigitsAfterDecimalPoint);
              const cropId = this._getCropTypeId(filteredPlanBooks);
              const appliedProducts = this.ppCommonService.getDistinctProductsOfPlanBooks(filteredPlanBooks);
              const isWorkTypeSpraying = this.ppCommonService.isWorkTypeSpraying(filteredPlanBooks?.FirstOrDefault());
              const planBookApplicationDates = this.planBooksService.getApplicationDatesData(filteredPlanBooks, this._isBooked);
              return {
                Id: summary.Id,
                Summary: summary,
                Products: undefined,
                Status: this.nutrientService.getFertilizerStatus(summary.ApplicationMode, this._isBooked)?.state,
                StatusClass: this.nutrientService.getFertilizerStatus(summary.ApplicationMode, this._isBooked)?.class,
                StatusDesc: this.nutrientService.getFertilizerStatusDescription(summary.ApplicationMode, this._isBooked),
                FieldIds: filteredPlanBooks.map(pb => pb.FieldId),
                Crop: cropTypesNames[Number.isFinite(cropId) ? +cropId : ''],
                CropId: cropId,
                Appl: this.ppCommonService.getApplNumName(this.ppCommonService.getApplNumFromSummary(summary)),
                ApplSum: isWorkTypeSpraying ? this.ppCommonService.getTotalWaterAmountBasedOnOperationMode(filteredPlanBooks) : PpNoDataValue,
                ApplUnit: isWorkTypeSpraying ? this.translationService.translate('Api_Enums__Litre') : undefined,
                Count: this._getValidPlansCountPerTotalPlansCount(totalPlanBooks),
                Area: area,
                OperationMode: this.translationService.translate(summary.ApplicationMode?.Key),
                OperationModeKey: summary.OperationMode.Key,
                OperationModeKeyTrans: this.translationService.translate(summary.OperationMode.Key),
                ProductNames: this.ppCommonService.getAppliedProductsExpr(appliedProducts),
                ProductNamesTooltip: this.ppCommonService.getAppliedProductsExpr(appliedProducts, true),
                ProductsSum: isWorkTypeSpraying ? this.ppCommonService.getAppliedProductSumExpr(appliedProducts, {
                  areaHa: area,
                  withoutProdName: true
                }) : undefined,
                ProductsSumTooltip: isWorkTypeSpraying ? this.ppCommonService.getAppliedProductSumExpr(appliedProducts, {
                  areaHa: area,
                  isTooltip: true
                }) : undefined,
                Avg: isWorkTypeSpraying ? summary.UserApplicationRate : PpNoDataValue,
                Min: isWorkTypeSpraying ? summary.UserApplicationMinimum : PpNoDataValue,
                Max: isWorkTypeSpraying ? summary.UserApplicationMaximum : PpNoDataValue,
                NUptakeAverage: nUptakeAverage,
                ApplicationDate: planBookApplicationDates?.NewestDate,
                ApplicationDateTooltip: planBookApplicationDates?.Tooltip,
                ApplicationDateItalic: planBookApplicationDates?.IsMoreThanOneUniqDate,
                Note: summary?.Title,
                PlanBooks: filteredPlanBooks,
                PlanNumber: summary?.PlanNumber,
                FieldsTooltip: this.tooltipContentService.getFieldsCropsTooltip(filteredPlanBooks)
              };
            });
        }
      )
    );
  }

  /**
   * Gets the loading indicator observable combined from all required stores
   */
  public getLoading$(): Observable<boolean> {
    return combineLatest([
      this.plantProtectionPlanBooksStore.Listen(s => s.loading),
      this.cropTypeStore.Listen(s => s.loading),
      this.fieldStore.Listen(s => s.loading),
      this.cropApplicationStore.Listen(s => s.loading)
    ]).pipe(
      map(items => items.some(x => x)
      )
    );
  }

  /**
   * Initializes and triggers load of planBooks
   */
  public subscribeLoad(): void {
    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe(() => {
      this.plantProtectionPlanBooksStore.loadPlanBooks();
      this.sensorStore.loadSensorFieldData();
    }));
  }

  /**
   * Initializes and triggers actions on selectionChange within the grid (dynComponent)
   * @param dynComponent the component's grid encapsulated in a dynComponent
   * @param options
   *  showRasterMap: true if raster should be shown in the map.
   *  Usually raster should not be shown by default for planning components
   */
  public subscribeSelection(dynComponent: ApDynComponentComponent, options = { showRasterMap: true }): void {
    this._subscriptions.push(dynComponent.SelectedItems$.subscribe((items: IPpPlanningBookingGridItem[]) => {
      this.mapStore.Layers.PpApplicationMapLayer.clear();
      const fieldIds = items.map((item) => item.FieldIds.map(f => f?.toString())).flat();
      this.fieldStore.changeSelectedField(fieldIds.filter((v, i, a) => a.indexOf(v) === i));
      const planBooks = items.map(item => item.PlanBooks).flat()
        .filter(pB => (this._isBooked ? this.isValidBooking(pB) : this.isValidPlanning(pB)));
      this.showPlanBooksInMap(planBooks);
      if (!options?.showRasterMap) {
        return;
      }
      const showRaster = planBooks.map((pb) => ({
        FieldId: pb.FieldId.toString(),
        PlanBookId: pb.Id.toString()
      }));
      this.showPointsOnMap.showAllRaster(showRaster, 'pp');
    }));
  }

  public showPlanBooksInMap(planBooks: IPlantProtectionPlanBook[]): void {
    const planBooksIds = planBooks.map(x => x.Id);
    if (planBooksIds.length === 0) {
      return;
    }

    this.mapStore.Layers.PpApplicationMapLayer.clear();
    const statistic = {
      Max: planBooks.Reduce((target, ele) => {
        if (ele.ApplicationMax > target) {
          return ele.ApplicationMax;
        }
        return target;
      }, 0),
      Min: planBooks.Reduce((target, ele) => {
        return target < ele.ApplicationMin ? target : ele.ApplicationMin;
      }, planBooks.FirstOrDefault().ApplicationMin)
    };
    this.mapStore.Legends.onPPApplicationChanged.emit(statistic);
    this.mapStore.Layers.PPApplicationLayerSourceInit(this.routerStore.Listen(s => s.url).getValue(), planBooksIds);
  }

  public destroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  private _getSummaryWithPlanBooks(planBooks: IPlantProtectionPlanBook[], isBooking: boolean): ISummaryWithPlanBooks[] {
    const summariesMap = planBooks.reduce((summaryMap, item) => {
      const id = item.Summary?.Id?.toString() ?? undefined;
      if (id === undefined) {
        return summaryMap;
      }
      if (summaryMap[id] === undefined) {
        summaryMap[id] = {Summary: item.Summary, FilteredPlanBooks: [], TotalPlanBooks: []};
      }
      if (!item.DeletedAt) {
        if (isBooking ? item.BookedAt : !item.BookedAt) {
          summaryMap[id].FilteredPlanBooks.push(item);
        }
        summaryMap[id].TotalPlanBooks.push(item);
      }
      return summaryMap;
    }, {} as Record<string, ISummaryWithPlanBooks>);
    return Object.keys(summariesMap).map((key) => summariesMap[key]);
  }

  private _getValidPlansCountPerTotalPlansCount(planBooks: IPlantProtectionPlanBook[]): string {
    if (!planBooks || planBooks.length <= 0) {
      return '0/0';
    }
    const nonDeletedPlanBooks = planBooks.filter(x => !x.DeletedAt);
    const filteredPlanBooks = nonDeletedPlanBooks.filter(p => this._isBooked ? p.BookedAt : !p.BookedAt);
    return filteredPlanBooks.length + '/' + nonDeletedPlanBooks.length;
  }

  private isValidBooking(planBook: IPlantProtectionPlanBook): boolean {
    return planBook.BookedAt && !planBook.DeletedAt;
  }

  private isValidPlanning(planBook: IPlantProtectionPlanBook): boolean {
    return !planBook.BookedAt && !planBook.DeletedAt;
  }

  private _getCropTypeId(planBooks: IPlantProtectionPlanBook[]): number {
    return planBooks.map(pB => pB.MainCropId).reduce((acc, el) => {
      acc.k[el] = acc.k[el] ? acc.k[el] + 1 : 1;
      acc.max = acc.max ? acc.max < acc.k[el] ? el : acc.max : el;
      return acc;
    }, {k: {}, max: undefined}).max;
  }
}
